import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCard } from '@/components/ui/SkeletonCard';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Eye,
  FileText,
  Building2,
} from 'lucide-react';
import { useDiligencias, type Diligencia, type DiligenciaEtapa, type DiligenciaStatus, useDiligenciaPermissions } from '@/features/diligencias/hooks';
import {
  ESPECIALIDADE_LABELS,
  AREA_STATUS_LABELS,
  ETAPA_LABELS,
  ETAPAS_ORDENADAS,
  getAreaStatus,
  type DiligenciaEspecialidade,
  type AreaStatus,
  type DiligenciaAreaStatuses,
} from '@/utils/diligenciaChecklists';
import { getEspecialidadeConfig } from '@/utils/adminEspecializadoConfig';
import { formatDate } from '@/lib/formatters';
import { useNavigate } from 'react-router-dom';
import { useDiligenciasBasePath } from '@/features/diligencias/hooks';

const statusLabels: Record<DiligenciaStatus, string> = {
  rascunho: 'Rascunho',
  pendente: 'Pendente',
  em_analise: 'Em análise',
  doc_pendente: 'Doc. Pendente',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  cancelado: 'Cancelado',
};

const getStatusVariant = (status: DiligenciaStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'aprovado':
      return 'default';
    case 'pendente':
    case 'em_analise':
      return 'secondary';
    case 'reprovado':
    case 'cancelado':
      return 'destructive';
    default:
      return 'outline';
  }
};

/** Total de etapas canônicas — fonte: ETAPAS_ORDENADAS em diligenciaChecklists. */
const TOTAL_ETAPAS = ETAPAS_ORDENADAS.length;

const FIRST_ETAPA_INDEX = 0;
const LAST_ETAPA_INDEX = TOTAL_ETAPAS - 1;

const getEtapaIndex = (etapa: DiligenciaEtapa): number => {
  return ETAPAS_ORDENADAS.indexOf(etapa);
};

export const GerenciarDiligencias: React.FC = () => {
  const navigate = useNavigate();
  const diligenciasBasePath = useDiligenciasBasePath();
  const {
    diligencias,
    isLoading,
    error,
    fetchAllDiligencias,
    avancarEtapa,
    retrocederEtapa,
    updateDiligenciaStatus,
  } = useDiligencias({ skipAutoFetch: true });
  const { canAdvanceRetreat, canApproveReject, isAdminEspecializado, canAccessEtapa, especialidade } = useDiligenciaPermissions();
  const espConfig = getEspecialidadeConfig(especialidade);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [etapaFilter, setEtapaFilter] = useState<string>('all');
  /** ID da diligência com ação em andamento (avançar, retroceder, aprovar, reprovar) */
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchAllDiligencias();
  }, [fetchAllDiligencias]);

  // Prioridade de status para ordenação (admin especializado)
  const statusPriority: Record<string, number> = {
    doc_pendente: 0,
    pendente: 1,
    em_analise: 2,
    rascunho: 3,
    aprovado: 4,
    reprovado: 5,
    cancelado: 6,
  };

  const filteredDiligencias = diligencias
    .filter((d) => {
      const matchesSearch =
        d.empreendimento_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.spe_razao_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.spe_cnpj?.includes(searchTerm);

      const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
      const matchesEtapa = etapaFilter === 'all' || d.etapa_atual === etapaFilter;

      // Admin especializado: filtrar para mostrar apenas diligências cuja etapa atual é acessível
      const matchesEspecialidade = !isAdminEspecializado || canAccessEtapa(d.etapa_atual);

      return matchesSearch && matchesStatus && matchesEtapa && matchesEspecialidade;
    })
    // Admin especializado: ordenar por prioridade de status
    .sort((a, b) => {
      if (!isAdminEspecializado) return 0;
      return (statusPriority[a.status] ?? 9) - (statusPriority[b.status] ?? 9);
    });

  const handleAvancar = async (id: string) => {
    setActionLoading(id);
    await avancarEtapa(id);
    setActionLoading(null);
  };

  const handleRetroceder = async (id: string) => {
    setActionLoading(id);
    await retrocederEtapa(id);
    setActionLoading(null);
  };

  const handleAprovar = async (id: string) => {
    setActionLoading(id);
    await updateDiligenciaStatus(id, 'aprovado');
    setActionLoading(null);
  };

  const handleReprovar = async (id: string) => {
    setActionLoading(id);
    await updateDiligenciaStatus(id, 'reprovado');
    setActionLoading(null);
  };

  if (isLoading && diligencias.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-foreground">Gerenciar Diligencias</h1>
        </div>
        <SkeletonCard count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-foreground">Gerenciar Diligencias</h1>
        </div>
        <ErrorState title="Erro ao carregar" message={error} onRetry={fetchAllDiligencias} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-foreground">
              {espConfig?.filaNome ?? 'Gerenciar Diligências'}
            </h1>
            {espConfig && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {espConfig.filaDescricao}
              </p>
            )}
          </div>
        </div>
        <Badge variant="outline" className="text-sm">
          {filteredDiligencias.length} diligência{filteredDiligencias.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por empreendimento, SPE ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={etapaFilter} onValueChange={setEtapaFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Etapa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Etapas</SelectItem>
                {ETAPAS_ORDENADAS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {ETAPA_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {espConfig ? `Diligências — ${espConfig.label}` : 'Lista de Diligências'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDiligencias.length === 0 ? (
            <EmptyState
              title="Nenhuma diligência encontrada"
              description={searchTerm || statusFilter !== 'all' || etapaFilter !== 'all'
                ? 'Tente ajustar os filtros de busca'
                : 'Nenhuma diligência cadastrada no momento'}
              icon={<Building2 className="w-12 h-12" />}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empreendimento</TableHead>
                    <TableHead>SPE</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdminEspecializado && <TableHead>Área</TableHead>}
                    <TableHead>{isAdminEspecializado ? 'Atualizado' : 'Criado em'}</TableHead>
                    <TableHead className="text-right">Acoes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDiligencias.map((diligencia) => {
                    // Status da área para admin especializado
                    let areaStatusValue: AreaStatus | null = null;
                    if (isAdminEspecializado && especialidade) {
                      const d = diligencia as Record<string, unknown>;
                      const areaStatuses: DiligenciaAreaStatuses = {
                        area_tecnica_economica_status: (d.area_tecnica_economica_status as AreaStatus) || 'pendente',
                        area_juridica_status: (d.area_juridica_status as AreaStatus) || 'pendente',
                        area_contabil_status: (d.area_contabil_status as AreaStatus) || 'pendente',
                        area_mercadologica_status: (d.area_mercadologica_status as AreaStatus) || 'pendente',
                      };
                      areaStatusValue = getAreaStatus(areaStatuses, diligencia.etapa_atual);
                    }

                    const areaStatusBadgeClass: Record<string, string> = {
                      pendente: 'bg-yellow-50 text-yellow-700 border-yellow-200',
                      em_analise: 'bg-blue-50 text-blue-700 border-blue-200',
                      concluida: 'bg-green-50 text-green-700 border-green-200',
                    };

                    return (
                      <TableRow key={diligencia.id}>
                      <TableCell className="font-medium">
                        {diligencia.empreendimento_nome}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{diligencia.spe_razao_social}</p>
                          <p className="text-xs text-muted-foreground">
                            {diligencia.spe_cnpj}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground">
                            {getEtapaIndex(diligencia.etapa_atual) + 1}/{TOTAL_ETAPAS}
                          </span>
                          <span className="text-sm">
                            {ETAPA_LABELS[diligencia.etapa_atual]}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(diligencia.status)}>
                          {statusLabels[diligencia.status]}
                        </Badge>
                      </TableCell>
                      {isAdminEspecializado && (
                        <TableCell>
                          {areaStatusValue && (
                            <Badge variant="outline" className={`text-xs border ${areaStatusBadgeClass[areaStatusValue] ?? ''}`}>
                              {AREA_STATUS_LABELS[areaStatusValue]}
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell>{formatDate(isAdminEspecializado ? diligencia.updated_at : diligencia.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`${diligenciasBasePath}/${diligencia.id}`)}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canAdvanceRetreat && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRetroceder(diligencia.id)}
                                disabled={
                                  actionLoading === diligencia.id ||
                                  getEtapaIndex(diligencia.etapa_atual) === FIRST_ETAPA_INDEX
                                }
                                title="Retroceder etapa"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAvancar(diligencia.id)}
                                disabled={
                                  actionLoading === diligencia.id ||
                                  getEtapaIndex(diligencia.etapa_atual) === LAST_ETAPA_INDEX
                                }
                                title="Avancar etapa"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canApproveReject && diligencia.status !== 'aprovado' && diligencia.status !== 'reprovado' && (
                            <>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleAprovar(diligencia.id)}
                                disabled={actionLoading === diligencia.id}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Aprovar
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleReprovar(diligencia.id)}
                                disabled={actionLoading === diligencia.id}
                              >
                                Reprovar
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GerenciarDiligencias;
